import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import config from 'src/config';
import { useToast } from 'src/context/ToastContext';
import { RebateAgreementListItem } from './types';

interface DeleteResult {
  agreementId: string;
  agreementName: string;
  success: boolean;
  error?: string;
}

interface BulkDeleteState {
  isLoading: boolean;
  results: DeleteResult[];
  successCount: number;
  failureCount: number;
}

/**
 * Hook for bulk deleting rebate agreements.
 * Executes deletions in parallel and reports success/failure counts.
 */
export function useBulkDeleteRebateAgreements() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [state, setState] = useState<BulkDeleteState>({
    isLoading: false,
    results: [],
    successCount: 0,
    failureCount: 0,
  });

  const bulkDelete = useCallback(
    async (agreements: RebateAgreementListItem[]) => {
      if (agreements.length === 0) return;

      setState({
        isLoading: true,
        results: [],
        successCount: 0,
        failureCount: 0,
      });

      const deletePromises = agreements.map(async (agreement) => {
        try {
          const response = await fetch(
            `${config.apiBaseUrl}/rebates/agreements/${agreement.id}`,
            {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
            },
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
              agreementId: agreement.id,
              agreementName: agreement.name,
              success: false,
              error:
                (errorData as { message?: string }).message ||
                'Failed to delete agreement',
            };
          }

          return {
            agreementId: agreement.id,
            agreementName: agreement.name,
            success: true,
          };
        } catch (error) {
          return {
            agreementId: agreement.id,
            agreementName: agreement.name,
            success: false,
            error:
              error instanceof Error ? error.message : 'Unknown error occurred',
          };
        }
      });

      const results = await Promise.all(deletePromises);
      const successCount = results.filter((r) => r.success).length;
      const failureCount = results.filter((r) => !r.success).length;

      setState({
        isLoading: false,
        results,
        successCount,
        failureCount,
      });

      // Show toast with results
      if (failureCount === 0) {
        showToast({
          message: `Successfully deleted ${successCount} agreement${successCount !== 1 ? 's' : ''}`,
          title: 'Bulk Delete Complete',
          color: 'success',
          icon: 'check',
        });
      } else if (successCount === 0) {
        showToast({
          message: `Failed to delete all ${failureCount} agreement${failureCount !== 1 ? 's' : ''}`,
          title: 'Bulk Delete Failed',
          color: 'error',
          icon: 'error',
        });
      } else {
        showToast({
          message: `Deleted ${successCount} of ${results.length} agreements. ${failureCount} failed.`,
          title: 'Bulk Delete Partially Complete',
          color: 'warning',
          icon: 'warning',
        });
      }

      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['rebateAgreements'] });
      queryClient.invalidateQueries({ queryKey: ['rebateAgreement'] });

      return results;
    },
    [queryClient, showToast],
  );

  const reset = useCallback(() => {
    setState({
      isLoading: false,
      results: [],
      successCount: 0,
      failureCount: 0,
    });
  }, []);

  return {
    bulkDelete,
    reset,
    isLoading: state.isLoading,
    results: state.results,
    successCount: state.successCount,
    failureCount: state.failureCount,
  };
}

export default useBulkDeleteRebateAgreements;
